/**
 * Overview: where a machine's page opens. What needs you (approvals answered in place, questions one step from their
 * session), what is running now, its workspaces and how each stands, and the machine's health in plain words. Every
 * row opens the native view for it (the session, Team Work); nothing here asks for the screen.
 */

import { riskLevelSentence, workspaceHostDown } from '@aioe/shared';
import { CircleExclamation, Clock, Comments, Folder, Pulse } from '@gravity-ui/icons';
import { useState, useSyncExternalStore } from 'react';

import { remotePath, routes } from '../../api/client.ts';
import { useAuth } from '../../auth/AuthProvider.tsx';
import { HostStateMark } from '../../components/hostState.tsx';
import { liveCall, liveProblem } from '../../components/live/useLiveSession.ts';
import { connectionOf, PolicyLine } from '../../components/machineWords.tsx';
import { postureInWords, postureWeak } from '../../components/meshWords.ts';
import { ArtEmpty, Button, ErrorNote, ListBlock, ListGroup, ListRow, Stat, StatusMark } from '../../components/ui.tsx';
import { isCloud } from '../../edition.ts';
import { useLoad } from '../../hooks.ts';
import { useMachine } from '../machineContext.ts';
import { useMachineWork, type MachineWait } from '../useMachineWork.ts';
import type { Workbench } from '../useWorkbench.ts';

const PLATFORM: Record<string, string> = { darwin: 'macOS', windows: 'Windows', linux: 'Linux' };

const tone = 'teal' as const;

export function OverviewView({ w, relay, admin }: { w: Workbench; relay: boolean; admin: boolean }) {
  const m = useMachine();
  const d = w.device.data;
  const { account } = useAuth();
  const work = useMachineWork(w);
  const via = useSyncExternalStore(remotePath.subscribe, remotePath.get);
  const conn = connectionOf(d, via);
  const mine = Boolean(d && account?.id && d.userId === account.id && d.kind !== 'node');
  const sessions = work.data?.sessions ?? [];
  const running = sessions.filter((s) => s.state === 'working' || s.waiting);
  const waits = work.data?.needsYou ?? [];
  const workspaces = d?.workspaces ?? [];
  const down = workspaces.filter((x) => workspaceHostDown(x)).length;
  // A stopped host's workspace says when it last ran: the project's root keeps that time (a stopped host does not move it).
  const downProjects = [...new Set(workspaces.filter((x) => x.projectId && workspaceHostDown(x)).map((x) => x.projectId!))];
  const lastRunning = useLoad(async () => {
    const out = new Map<string, string>();
    await Promise.all(downProjects.map((id) => routes.project(id).then((p) => p.roots.forEach((r) => r.lastSeenAt && out.set(`${id}/${r.rootId}`, r.lastSeenAt))).catch(() => undefined)));
    return out;
  }, [downProjects.join(',')]);
  const needNames = workspaces.some((x) => x.projectId);
  const projectNames = useLoad(() => (needNames ? routes.projects().then((ps) => new Map(ps.map((p) => [p.id, p.name]))).catch(() => new Map<string, string>()) : Promise.resolve(new Map<string, string>())), [needNames]);
  const posture = postureInWords(d?.posture);

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div className="art-stats">
        <Stat tone="amber" icon={<CircleExclamation />} label="Needs you" value={relay ? (work.data ? waits.length : undefined) : undefined} detail={!relay ? 'With Teams' : work.data?.live ? (waits.length ? 'Approvals and questions' : 'Nothing waiting') : w.connected ? 'Asking' : 'As last reported'} needsYou={waits.length > 0} />
        <Stat tone={tone} icon={<Pulse />} label="Working now" value={relay ? (work.data ? running.length : undefined) : d?.activeAgents} detail={sessions.length ? `Of ${sessions.length} ${sessions.length === 1 ? 'session' : 'sessions'}` : 'Agents at work'} />
        <Stat tone="blue" icon={<Folder />} label="Workspaces" value={d ? workspaces.length : undefined} detail={d ? (down ? `${down} stopped` : workspaces.length ? 'All running' : 'None reported') : ''} />
        <Stat tone="slate" icon={<Clock />} label="Last seen" value={d ? (d.relayConnected ? 'Now' : conn.detail.replace(/^last seen /, '')) : undefined} detail={conn.label} />
      </div>

      {relay && waits.length ? (
        <ListGroup title="Needs you" count={waits.length} description="Approvals answered here; questions and reviews open in their session.">
          {waits.map((x) => (
            <WaitRow key={`${x.kind}:${x.id}`} w={w} x={x} canAnswer={mine} onDone={work.reload} />
          ))}
        </ListGroup>
      ) : null}

      {relay ? (
        <ListGroup
          title="Running now"
          count={running.length || undefined}
          description="Sessions at work or waiting on someone. Open one to follow it."
          actions={
            sessions.length ? (
              <Button size="sm" variant="ghost" onPress={() => m.go('sessions')}>
                All sessions
              </Button>
            ) : undefined
          }
          empty={
            running.length ? undefined : (
              <ArtEmpty compact tone={tone} icon={<Comments />} title={work.data ? 'Nothing running right now' : w.connected ? `Asking ${m.name}…` : `${m.subject} is not connected`}>
                {work.data ? (sessions.length ? 'Its sessions are idle. Open one to carry on.' : 'Chats and team work started on this machine show here.') : w.connected ? undefined : 'Its sessions show here while it runs.'}
              </ArtEmpty>
            )
          }
        >
          {running.slice(0, 8).map((s) => (
            <ListRow key={s.key} title={s.title} description={s.line || undefined} status={<StatusMark state={s.waiting ? 'medium' : s.state} label={s.waiting ? 'Needs you' : s.words} />} onPress={() => m.go('sessions', { session: s.key })} />
          ))}
        </ListGroup>
      ) : (
        <ListGroup title="Sessions">
          <ListRow title="Following this machine’s sessions" description="From the web and the phone, this comes with Teams." />
        </ListGroup>
      )}

      <ListGroup
        title="Workspaces"
        count={workspaces.length || undefined}
        description="The repositories open on this machine, and how each is running."
        empty={
          workspaces.length ? undefined : (
            <ArtEmpty compact tone="blue" icon={<Folder />} title="No workspaces reported yet">
              The repositories open on this machine show here with how each is running.
            </ArtEmpty>
          )
        }
      >
        {workspaces.map((x) => (
          <ListRow
            key={x.id}
            title={x.name}
            description={x.projectId ? (projectNames.data?.get(x.projectId) ?? 'In a project') : 'Not in a project'}
            status={<HostStateMark root={x} lastSeenAt={x.projectId ? lastRunning.data?.get(`${x.projectId}/${x.id}`) : undefined} />}
          />
        ))}
      </ListGroup>

      <ListGroup title="Health" description="How it is reached, what it runs and the policy it follows.">
        <ListRow title="Connection" description={conn.detail} status={<StatusMark state={conn.state} label={conn.label} />} />
        <ListRow title={d ? `${d.kind === 'node' ? 'Node' : 'AI Workbench'} ${d.appVersion}` : 'Version'} description={d ? `On ${PLATFORM[d.platform] ?? d.platform}${d.posture?.osVersion ? ` ${d.posture.osVersion}` : ''}.` : '—'} />
        <ListRow title="Security" description={`${posture[0]!.toUpperCase()}${posture.slice(1)}.`} status={postureWeak(d?.posture) ? <StatusMark state="medium" label="Needs attention" /> : undefined} />
        {!isCloud() || admin ? <ListRow title="Policy" description={<PolicyLine revision={d?.policyRevision} admin={admin} />} status={d?.policyUnknownKeys?.length ? <StatusMark state="medium" label="Update needed" /> : undefined} /> : null}
        {d?.policyUnknownKeys?.length ? <ListBlock className="text-xs text-muted">It is too old for part of its policy; updating it applies the rest.</ListBlock> : null}
      </ListGroup>
    </div>
  );
}

/** One thing that waits: an approval answered here, or a question opened in its session. */
function WaitRow({ w, x, canAnswer, onDone }: { w: Workbench; x: MachineWait; canAnswer: boolean; onDone: () => void }) {
  const m = useMachine();
  const [busy, setBusy] = useState<'allow' | 'deny'>();
  const [problem, setProblem] = useState<string>();
  const answer = async (decision: 'allow' | 'deny') => {
    setBusy(decision);
    setProblem(undefined);
    try {
      await liveCall(w.deviceId, 'permissions.resolve', { ...(x.sessionId ? { sessionId: x.sessionId } : {}), permissionId: x.id, decision, via: 'web' });
      onDone();
    } catch (e) {
      setProblem(liveProblem(e, m.subject));
    } finally {
      setBusy(undefined);
    }
  };
  const ownerOnly = (x.riskLevel ?? 0) >= 4;
  const open = () => (x.sessionKey ? m.go('sessions', { session: x.sessionKey }) : m.go('team-work'));
  return (
    <ListRow
      title={
        <button type="button" onClick={open} className="cursor-[var(--cursor-interactive)] text-left [overflow-wrap:anywhere] hover:underline">
          {x.title}
        </button>
      }
      description={
        <>
          {[x.kind === 'approval' ? 'Asks to use a tool' : x.kind === 'question' ? 'Asks a question' : 'Ready for review', x.line].filter(Boolean).join(' · ')}
          {x.kind === 'approval' && !(canAnswer && !ownerOnly) && !ownerOnly ? <span className="block">Only the person it runs for can answer this.</span> : null}
        </>
      }
      status={x.riskLevel ? <StatusMark wrap state={x.riskLevel <= 2 ? 'low' : x.riskLevel === 3 ? 'medium' : 'high'} label={riskLevelSentence(x.riskLevel)} /> : undefined}
      control={
        x.kind === 'approval' && canAnswer && !ownerOnly ? (
          <>
            <Button size="sm" variant="danger-soft" isPending={busy === 'deny'} isDisabled={Boolean(busy)} onPress={() => void answer('deny')}>
              Refuse
            </Button>
            <Button size="sm" variant="primary" isPending={busy === 'allow'} isDisabled={Boolean(busy)} onPress={() => void answer('allow')}>
              Approve
            </Button>
          </>
        ) : x.kind !== 'approval' || ownerOnly ? (
          <Button size="sm" variant="secondary" onPress={open}>
            {x.kind === 'approval' ? 'Open the session' : x.kind === 'question' ? 'Answer' : 'Review'}
          </Button>
        ) : undefined
      }
    >
      {problem ? <ErrorNote error={problem} /> : null}
    </ListRow>
  );
}
